import { ValidationError } from "../errors";
import { notifySessionExpired } from "../utils/session";
import { requireTenant } from "../utils/tenant";
import { browserStore } from "../utils/storageKeys";

const apiBaseUrl: string = process.env.API_URL || "https://api.ediphi.com";
export interface fetchArgs {
  table: string;
  id?: string | null;
  subRoute?: string | null;
  params?: Record<string, string | number | string[]>;
  method?: "GET" | "POST";
}

function buildQueryString(params: Record<string, string | string[] | number>) {
  const queryStrings = Object.entries(params).flatMap(([key, value]) => {
    if (Array.isArray(value)) {
      return value.map((v) => `${encodeURIComponent(key)}=${encodeURIComponent(v)}`);
    }
    return `${encodeURIComponent(key)}=${encodeURIComponent(value)}`;
  });
  return queryStrings.length ? `?${queryStrings.join("&")}` : "";
}

export const fetchData = async ({
  table,
  id = null,
  subRoute = null,
  params,
  method = "GET",
}: fetchArgs): Promise<any> => {
  const xAuthToken = browserStore.get("xAuthToken");
  const tenant = requireTenant();

  if (!xAuthToken) {
    throw new ValidationError("An auth token is required to fetch data.");
  }

  const apiUrl = `${apiBaseUrl}/api/${table}${id ? `/${id}` : ""}${subRoute ? `/${subRoute}` : ""}${buildQueryString(params || {})}`;

  try {
    const apiResponse = await fetch(apiUrl, {
      method,
      headers: {
        Authorization: `Bearer ${xAuthToken}`,
        tenant,
      },
    });

    // A 401 on a request we authenticated with a stored token means that token was revoked
    // (e.g. a web-app logout revoked all the user's tokens) or expired — signal a clean re-login.
    if (apiResponse.status === 401) {
      notifySessionExpired();
    }

    if (!apiResponse.ok) {
      let errorMessage = "API request failed";
      try {
        const errorBody = await apiResponse.json();
        if (errorBody.message || errorBody.error) {
          errorMessage = errorBody.message || errorBody.error;
        }
      } catch {
        // If response body isn't JSON, use default message
      }

      const error = new Error(errorMessage) as Error & { statusCode: number; statusText: string };
      error.statusCode = apiResponse.status;
      error.statusText = apiResponse.statusText;
      throw error;
    }

    return await apiResponse.json();
  } catch (error) {
    //console.warn("Error fetching data:", error);
    // ???: Do we want to throw errors - if we swallow here the api layer will never know there was an error
    // throw error;
  }
};

/**
 * Like fetchData, but treats the swallowed-error `undefined` result as a failure.
 * fetchData resolves undefined only when the request failed — JSON can never encode
 * undefined — so this restores a throwing contract for callers that must distinguish
 * "request failed" from "server says empty" (the Result-based api/ fetchers).
 */
export const fetchDataStrict = async (args: fetchArgs): Promise<any> => {
  const response = await fetchData(args);
  if (response === undefined) {
    throw new Error("API request failed");
  }
  return response;
};

export interface writeArgs {
  table: string;
  id?: string | null;
  subRoute?: string | null;
  payload: object;
  method?: "POST" | "PUT";
}

export const writeData = async ({
  table,
  id = null,
  subRoute = null,
  payload,
  method = "POST",
}: writeArgs): Promise<any> => {
  const xAuthToken = browserStore.get("xAuthToken");
  const tenant = requireTenant();

  if (!xAuthToken) {
    throw new ValidationError("An auth token is required to write data.");
  }

  const apiUrl = `${apiBaseUrl}/api/${table}${id ? `/${id}` : ""}${subRoute ? `/${subRoute}` : ""}`;

  const apiResponse = await fetch(apiUrl, {
    method,
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${xAuthToken}`,
      tenant,
    },
    body: JSON.stringify(payload),
  });

  // See fetchData: a 401 on a token-authenticated write means the session was revoked or expired.
  if (apiResponse.status === 401) {
    notifySessionExpired();
  }

  if (!apiResponse.ok) {
    let errorMessage = "API request failed";
    try {
      const errorBody = await apiResponse.json();
      // Ediphi API returns { error: true, data: "message" }
      if (errorBody.data && typeof errorBody.data === "string") {
        errorMessage = errorBody.data;
      } else if (errorBody.message) {
        errorMessage = errorBody.message;
      }
    } catch {
      // If response body isn't JSON, use default message
    }

    const error = new Error(`${apiResponse.status} ${errorMessage}`) as Error & {
      statusCode: number;
      statusText: string;
    };
    error.statusCode = apiResponse.status;
    error.statusText = apiResponse.statusText;
    throw error;
  }

  return await apiResponse.json();
};

export interface writeFormDataArgs {
  table: string;
  id?: string | null;
  subRoute?: string | null;
  params?: Record<string, string | number | string[]>;
  formData: FormData;
}

/**
 * Multipart POST. No `Content-Type` header — the browser must set it so the multipart boundary is
 * generated, which is why this cannot go through `writeData`.
 */
export const writeFormData = async ({
  table,
  id = null,
  subRoute = null,
  params,
  formData,
}: writeFormDataArgs): Promise<any> => {
  const xAuthToken = browserStore.get("xAuthToken");
  const tenant = requireTenant();

  if (!xAuthToken) {
    throw new ValidationError("An auth token is required to write data.");
  }

  const apiUrl = `${apiBaseUrl}/api/${table}${id ? `/${id}` : ""}${subRoute ? `/${subRoute}` : ""}${buildQueryString(params || {})}`;

  const apiResponse = await fetch(apiUrl, {
    method: "POST",
    headers: { Authorization: `Bearer ${xAuthToken}`, tenant },
    body: formData,
  });

  if (apiResponse.status === 401) {
    notifySessionExpired();
  }

  if (!apiResponse.ok) {
    let errorMessage = "API request failed";
    try {
      const errorBody = await apiResponse.json();
      // Ediphi API returns { error: true, data: "message" }
      if (typeof errorBody?.data === "string" && errorBody.data) {
        errorMessage = errorBody.data;
      } else if (typeof errorBody?.message === "string" && errorBody.message) {
        errorMessage = errorBody.message;
      } else if (typeof errorBody?.error === "string" && errorBody.error) {
        errorMessage = errorBody.error;
      }
    } catch {
      // non-JSON body
    }

    const error = new Error(errorMessage) as Error & { statusCode: number; statusText: string };
    error.statusCode = apiResponse.status;
    error.statusText = apiResponse.statusText;
    throw error;
  }

  return await apiResponse.json();
};
